import React, { useEffect, useRef } from "react";
import FeedMediaComposer from "./FeedMediaComposer";

export default function PostInputComposer({
  avatarUrl,
  value,
  onChange,
  onSubmit,
  onCancel,
  showCancel = false,
  placeholder = "What's happening?",
  submitLabel = "Post",
  autoFocus = false,
  isPosting = false,
  mediaFiles = [],
  mediaPreview = [],
  uploadProgress = {},
  onSelectMedia,
  showMedia = false,
  inlineActions = false,
}) {
  const textareaRef = useRef(null);
  useEffect(() => {
    if (autoFocus) {
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    }
  }, [autoFocus]);

  // Hide the global bottom tab bar while this inline composer is mounted
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("keyboard-compose");
    return () => root.classList.remove("keyboard-compose");
  }, []);

  return (
    <div className="px-3 sm:px-4 py-3 border-b border-neutral-200 dark:border-neutral-700 flex gap-3">
      {/* AVATAR */}
      <div className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 mt-0.5">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt="Me"
            className="w-full h-full rounded-full object-cover"
          />
        ) : (
          <div className="w-full h-full rounded-full bg-neutral-300 dark:bg-neutral-700 flex items-center justify-center text-sm">
            👤
          </div>
        )}
      </div>

      {/* INPUT COLUMN */}
      <div className="flex-1 min-w-0">
        <textarea
          ref={textareaRef}
          autoComplete="off"
          dir="ltr"
          rows={2}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full resize-none bg-transparent outline-none text-[15px] sm:text-base leading-relaxed text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500"
        />

        {/* MEDIA + ACTIONS ROW */}
        {showMedia && (
          <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
            {/* MEDIA SELECT */}
            <FeedMediaComposer
              mediaFiles={mediaFiles}
              mediaPreview={mediaPreview}
              uploadProgress={uploadProgress}
              isPosting={isPosting}
              onSelectMedia={onSelectMedia}
              showProgress
              compact
            />

            {/* ACTION BUTTONS */}
            <div className="flex items-center gap-2 ml-auto">
              {showCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-3 sm:px-4 py-1.5 rounded-full text-sm text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:scale-95 transition-all touch-manipulation"
                  style={{ minHeight: 36 }}
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                disabled={isPosting || (!value.trim() && mediaFiles.length === 0)}
                onClick={onSubmit}
                className={[
                  "px-4 sm:px-5 py-1.5 rounded-full font-semibold text-sm transition-all active:scale-95 touch-manipulation",
                  isPosting || (!value.trim() && mediaFiles.length === 0)
                    ? "bg-blue-300 text-white opacity-60 cursor-not-allowed"
                    : "bg-blue-500 hover:bg-blue-600 text-white",
                ].join(" ")}
                style={{ minHeight: 36 }}
              >
                {isPosting ? "Posting…" : submitLabel}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}